import type { z } from 'zod'
import { tarslogs, tarslogsEnabled } from '@/plugins/tarslogs'

interface ApiError {
  error?: string | { message?: string }
  message?: string
}

function getApiErrorMessage(data: unknown, status: number): string {
  if (!data || typeof data !== 'object') return `Request failed: ${status}`

  const apiError = data as ApiError
  if (typeof apiError.error === 'string') return apiError.error
  if (apiError.error && typeof apiError.error.message === 'string') return apiError.error.message
  if (typeof apiError.message === 'string') return apiError.message
  return `Request failed: ${status}`
}

export function useApi() {
  const baseUrl = import.meta.env.VITE_API_URL || '/api'

  async function request<T>(
    path: string,
    options?: RequestInit,
    schema?: z.ZodSchema<T>
  ): Promise<T> {
    const url = `${baseUrl}${path}`
    const method = options?.method || 'GET'
    const logPath = path.split('?', 1)[0]
    let res: Response

    try {
      res = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers
        }
      })
    } catch (cause) {
      if (tarslogsEnabled) {
        const error = cause instanceof Error ? cause : new Error(String(cause))
        tarslogs.captureError(error, {
          source: 'api-fetch',
          failureType: 'network',
          method,
          path: logPath,
        })
        void tarslogs.flush()
      }
      throw cause
    }

    let data: unknown
    try {
      data = await res.json()
    } catch {
      const error = new Error(`API returned invalid JSON: ${res.status}`)
      if (tarslogsEnabled) {
        tarslogs.captureError(error, {
          source: 'api-response',
          failureType: 'invalid-json',
          method,
          path: logPath,
          status: res.status,
        })
        void tarslogs.flush()
      }
      throw error
    }

    if (!res.ok) {
      const errorMsg = getApiErrorMessage(data, res.status)
      if (tarslogsEnabled) {
        tarslogs.captureError(new Error(errorMsg), {
          source: 'api-response',
          failureType: 'http',
          method,
          path: logPath,
          status: res.status,
        })
        void tarslogs.flush()
      }
      throw new Error(errorMsg)
    }

    if (!schema) return data as T

    try {
      return schema.parse(data)
    } catch (cause) {
      if (tarslogsEnabled) {
        tarslogs.captureError(new Error('API response schema validation failed'), {
          source: 'api-response',
          failureType: 'schema-validation',
          method,
          path: logPath,
          status: res.status,
        })
        void tarslogs.flush()
      }
      throw cause
    }
  }

  return {
    get: <T>(path: string, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'GET' }, schema),

    post: <T>(path: string, body: unknown, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'POST', body: JSON.stringify(body) }, schema),

    put: <T>(path: string, body: unknown, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'PUT', body: JSON.stringify(body) }, schema),

    patch: <T>(path: string, body: unknown, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }, schema),

    delete: <T>(path: string, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'DELETE' }, schema)
  }
}
